import * as R from 'ramda';
import moment from 'moment';

import {initialState} from './index';

const {
    countries,
    contentTypeSpecialties,
    languages,
    regions,
    publisherTypes,
    campaignTypes,
    scoringStrategies,
    allBrands
} = window.sn.filters;

export default function FilterProcessor() {
    const registrationStatuses = [
        {value: 'yes', label: 'registered'},
        {value: 'no', label: 'unregistered'}
    ];

    const invertibleFilters = [
        ['has_approved_invite_for_campaigns', 'has_no_approved_invite_for_campaigns'],
        ['content_type_specialty', 'exclude_content_type_specialty'],
        ['language', 'exclude_language'],
        ['lead_country', 'exclude_lead_country'],
        ['profile_country', 'exclude_profile_country'],
        ['publisher_type', 'exclude_publisher_type'],
        ['age_range', 'exclude_age_range']
    ];

    function buildAgeRange(minField, maxField) {
        const min = parseInt(minField, 10);
        const max = parseInt(maxField, 10);
        if (!isNaN(min) && !isNaN(max)) {
            return min < max ? `${min}-${max}` : `${max}-${min}`;
        }
    }

    /**
     * @param {Object} filters
     * @return {*}
     */
    function encodeInvertibleFilters(filters) {
        return R.reduce(
            (newFilters, [filterName, invertedFilterName]) =>
                R.pipe(
                    R.ifElse(
                        R.prop(`invert_${filterName}`),
                        R.pipe(
                            R.assoc(invertedFilterName, newFilters[filterName]),
                            R.dissoc(filterName)
                        ),
                        R.identity
                    ),
                    R.dissoc(`invert_${filterName}`)
                )(newFilters),
            filters,
            invertibleFilters
        );
    }

    const encodeMultiSelectValues = R.ifElse(
        R.isNil,
        R.identity,
        R.pipe(R.map(R.prop('value')), R.join(','))
    );

    const encodeSelectValue = R.ifElse(R.isNil, R.identity, R.prop('value'));

    const encodeSelectKeyValues = R.map(
        R.evolve({
            key: encodeSelectValue
        })
    );

    const encodeMultiSelectKeyValues = R.map(
        R.evolve({
            key: encodeMultiSelectValues
        })
    );

    this.encodeFilter = function encodeFilter(filter) {
        const {
            age_range_min,
            age_range_max,
            children_age_range_min,
            children_age_range_max,
            demographics_age_range_min,
            demographics_age_range_max
        } = filter;

        return R.pipe(
            R.evolve({
                include_key_value_tags_v2: encodeSelectKeyValues,
                exclude_key_value_tags_v2: encodeSelectKeyValues,
                include_content_analysis_tags: encodeMultiSelectKeyValues,
                exclude_content_analysis_tags: encodeMultiSelectKeyValues,
                exclude_tags: encodeMultiSelectValues,
                include_tags: encodeMultiSelectValues,
                include_interests: encodeMultiSelectValues,
                exclude_interests: encodeMultiSelectValues,
                has_approved_invite_for_campaigns: encodeMultiSelectValues,
                has_no_invite_for_campaigns: encodeMultiSelectValues,
                has_approved_post_for_campaigns: encodeMultiSelectValues,
                metrics_platform: encodeSelectValue,
                scoring_strategy: encodeSelectValue,
                boost_creators_for_brand_id: encodeSelectValue,
                content_type_specialty: encodeMultiSelectValues,
                language: encodeMultiSelectValues,
                lead_country: encodeMultiSelectValues,
                profile_country: encodeMultiSelectValues,
                publisher_type: encodeMultiSelectValues,
                is_parent: encodeSelectValue,
                region: encodeMultiSelectValues,
                has_lead_for_platform: encodeMultiSelectValues,
                has_auth_for_platform: encodeMultiSelectValues,
                demographics_countries: encodeMultiSelectValues,
                demographics_include_interests: encodeMultiSelectValues,
                demographics_publisher_types: encodeMultiSelectValues,
                registered: encodeSelectValue
            }),
            f =>
                R.assoc(
                    'include_key_value_tags_v2_encoded',
                    JSON.stringify(f.include_key_value_tags_v2),
                    f
                ),
            f =>
                R.assoc(
                    'exclude_key_value_tags_v2_encoded',
                    JSON.stringify(f.exclude_key_value_tags_v2),
                    f
                ),
            f =>
                R.assoc(
                    'include_content_analysis_tags_encoded',
                    JSON.stringify(f.include_content_analysis_tags),
                    f
                ),
            f =>
                R.assoc(
                    'exclude_content_analysis_tags_encoded',
                    JSON.stringify(f.exclude_content_analysis_tags),
                    f
                ),
            f => R.assoc('demographics_cities_encoded', JSON.stringify(f.demographics_cities), f),
            R.assoc(
                'demographics_age_range',
                buildAgeRange(demographics_age_range_min, demographics_age_range_max)
            ),
            R.assoc('age_range', buildAgeRange(age_range_min, age_range_max)),
            R.assoc(
                'children_age_range',
                buildAgeRange(children_age_range_min, children_age_range_max)
            ),
            R.omit([
                'demographics_age_range_min',
                'demographics_age_range_max',
                'age_range_min',
                'age_range_max',
                'children_age_range_min',
                'children_age_range_max',
                'last_imported_start_moment',
                'last_imported_end_moment'
            ]),
            encodeInvertibleFilters
        )(filter);
    };

    /**
     * @param {string} filter
     * @param {string} invertedFilter
     * @param {Object} filters
     */
    function parseInvertibleFilter(filter, invertedFilter, filters) {
        return R.ifElse(
            R.both(R.complement(R.isNil), R.pipe(R.prop(invertedFilter), R.complement(R.isNil))),
            R.pipe(
                f => R.assoc(filter, f[invertedFilter], f),
                R.assoc(`invert_${filter}`, true),
                R.dissoc(invertedFilter)
            ),
            R.identity
        )(filters);
    }

    /**
     * @param {Object} filters
     * @return {*}
     */
    function parseInvertibleFilters(filters) {
        return R.reduce(
            (newFilters, [filter, invertedFilter]) =>
                parseInvertibleFilter(filter, invertedFilter, newFilters),
            filters,
            invertibleFilters
        );
    }

    const parseSimpleSelectValues = R.ifElse(
        R.isNil,
        R.identity,
        R.pipe(
            R.split(','),
            R.reject(R.equals('')),
            R.map(value => ({value, label: value}))
        )
    );

    const parseSimpleSelectValue = R.ifElse(R.isNil, R.identity, value => ({value, label: value}));

    const parseMultiSelectValues = R.curry(function(
        valueKey,
        labelKey,
        options,
        selectedOptionTransform,
        selectedOptions
    ) {
        if (R.isNil(selectedOptions)) return selectedOptions;
        const findLabel = val => R.path([labelKey], R.find(R.propEq(valueKey, val), options));
        return R.pipe(
            R.split(','),
            R.map(selectedOptionTransform),
            R.map(val => ({value: val, label: findLabel(val)})),
            R.reject(R.pipe(R.prop('label'), R.isNil))
        )(selectedOptions);
    });

    const parseSelectValue = R.curry(function(
        valueKey,
        labelKey,
        options,
        selectedOptionTransform,
        selectedOption
    ) {
        if (R.isNil(selectedOption)) return selectedOption;
        const findLabel = val => R.path([labelKey], R.find(R.propEq(valueKey, val), options));
        return R.pipe(
            selectedOptionTransform,
            val => ({value: val, label: findLabel(val)}),
            R.ifElse(R.pipe(R.prop('label'), R.isNil), R.always(null), R.identity)
        )(selectedOption);
    });

    const parseJsonString = R.ifElse(
        R.pipe(R.type, R.equals('String')),
        val => JSON.parse(val),
        R.identity
    );

    const parseSelectKeyValue = R.pipe(
        parseJsonString,
        R.map(
            R.evolve({
                key: parseSimpleSelectValue
            })
        )
    );

    const parseMultiSelectKeyValues = R.pipe(
        parseJsonString,
        R.map(
            R.evolve({
                key: parseSimpleSelectValues
            })
        )
    );

    this.mergeProps = function mergeProps(filters, allCampaigns = []) {
        const mergedProps = R.merge(initialState, filters);
        return this.parseFilter(mergedProps, allCampaigns);
    };

    this.parseFilter = function parseFilter(filters, allCampaigns = []) {
        const [last_imported_start_moment, last_imported_end_moment] = [
            R.path(['last_imported_at_start'], filters)
                ? moment(R.prop('last_imported_at_start', filters), 'YYYY-MM-DD')
                : null,
            R.path(['last_imported_at_end'], filters)
                ? moment(R.prop('last_imported_at_end', filters), 'YYYY-MM-DD')
                : null
        ];

        let isParent = null;
        if (filters) {
            if (!R.isNil(filters.is_parent) && !isNaN(parseInt(filters.is_parent, 10))) {
                isParent = parseInt(filters.is_parent, 10) ? 'yes' : 'no';
            } else {
                isParent = filters.is_parent;
            }
        }

        return R.pipe(
            parseInvertibleFilters,
            R.defaultTo({}),
            R.reduce(
                (updatedFilters, field) => {
                    const range = updatedFilters[field];
                    if (range) {
                        const [min, max] = range ? R.split('-', range) : [null, null];
                        return R.merge(updatedFilters, {
                            [`${field}_min`]: min,
                            [`${field}_max`]: max
                        });
                    }
                    return updatedFilters;
                },
                R.__,
                ['age_range', 'children_age_range', 'demographics_age_range']
            ),
            R.merge(R.__, {
                last_imported_start_moment,
                last_imported_end_moment,
                is_parent: isParent
            }),
            R.evolve({
                include_key_value_tags_v2: parseSelectKeyValue,
                exclude_key_value_tags_v2: parseSelectKeyValue,
                include_content_analysis_tags: parseMultiSelectKeyValues,
                exclude_content_analysis_tags: parseMultiSelectKeyValues,
                has_phone_number: JSON.parse,
                has_complete_profile: JSON.parse,
                has_incomplete_profile: JSON.parse,
                exclude_tags: parseSimpleSelectValues,
                include_tags: parseSimpleSelectValues,
                include_interests: parseSimpleSelectValues,
                exclude_interests: parseSimpleSelectValues,
                has_approved_invite_for_campaigns: parseMultiSelectValues(
                    'id',
                    'name',
                    allCampaigns,
                    parseInt
                ),
                has_no_invite_for_campaigns: parseMultiSelectValues(
                    'id',
                    'name',
                    allCampaigns,
                    parseInt
                ),
                has_approved_post_for_campaigns: parseMultiSelectValues(
                    'id',
                    'name',
                    allCampaigns,
                    parseInt
                ),
                metrics_platform: parseSelectValue('value', 'text', campaignTypes, R.identity),
                scoring_strategy: parseSelectValue('value', 'text', scoringStrategies, R.identity),
                boost_creators_for_brand_id: parseSelectValue('id', 'name', allBrands, parseInt),
                content_type_specialty: parseMultiSelectValues(
                    'value',
                    'text',
                    contentTypeSpecialties,
                    R.identity
                ),
                language: parseMultiSelectValues('code', 'name', languages, R.identity),
                lead_country: parseMultiSelectValues('code', 'name', countries, R.identity),
                profile_country: parseMultiSelectValues('code', 'name', countries, R.identity),
                publisher_type: parseMultiSelectValues('value', 'text', publisherTypes, R.identity),
                is_parent: parseSimpleSelectValue,
                region: parseMultiSelectValues('code', 'name', regions, R.identity),
                has_lead_for_platform: parseMultiSelectValues(
                    'value',
                    'text',
                    campaignTypes,
                    R.identity
                ),
                has_auth_for_platform: parseMultiSelectValues(
                    'value',
                    'text',
                    campaignTypes,
                    R.identity
                ),
                demographics_countries: parseMultiSelectValues(
                    'code',
                    'name',
                    countries,
                    R.identity
                ),
                demographics_cities: parseJsonString,
                demographics_include_interests: parseSimpleSelectValues,
                demographics_publisher_types: parseMultiSelectValues(
                    'value',
                    'text',
                    publisherTypes,
                    R.identity
                ),
                registered: parseSelectValue('value', 'label', registrationStatuses, R.identity)
            })
        )(filters);
    };

    /**
     * @param {*} filters
     * @param {Array} allCampaigns
     */
    this.prepareFilter = (filters, allCampaigns = []) => {
        const filterMap = {
            include_key_value_tags_v2_encoded: 'include_key_value_tags_v2',
            exclude_key_value_tags_v2_encoded: 'exclude_key_value_tags_v2',
            include_content_analysis_tags_encoded: 'include_content_analysis_tags',
            exclude_content_analysis_tags_encoded: 'exclude_content_analysis_tags',
            demographics_cities_encoded: 'demographics_cities'
        };

        const keys = R.intersection(R.keys(filterMap), R.keys(filters));
        const result = R.reduce(
            (newFilters, key) => {
                if (newFilters[key]) {
                    return R.assoc(filterMap[key], decodeURIComponent(newFilters[key]), newFilters);
                }
                return newFilters;
            },
            filters,
            keys
        );

        return this.parseFilter(result, allCampaigns);
    };
}



// WEBPACK FOOTER //
// ./publisher_leads/advanced_filters/filter_processor.js